import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DESIGN_REQUESTS } from "@/constants/designRequestTestIds";

/**
 * Admin-only debug modal — shows the exact system + user prompt that
 * the Assembly Agent would send to Claude for this design request,
 * without burning an LLM call. Useful for tuning Brand DNA, Reference
 * Creatives, and Content Selections.
 */
export default function PreviewPromptModal({ brandId, designRequestId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/brands/${brandId}/design-requests/${designRequestId}/preview-prompt`
        );
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || "Could not load prompt preview");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [brandId, designRequestId]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); } catch { /* ignore */ }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center p-4 sm:p-10 bg-[#1A1F2C]/60"
      onClick={onClose}
      data-testid={DESIGN_REQUESTS.previewPromptModal}
    >
      <div
        className="bg-[#F8F5EE] rounded-lg w-full max-w-4xl max-h-full overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-[#1A1F2C]/10 flex items-baseline justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#B5462F]">Debug</p>
            <h2 className="font-serif text-2xl mt-0.5">Preview LLM prompt</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={DESIGN_REQUESTS.previewPromptClose}
            className="text-sm text-[#6B6358] hover:text-[#1A1F2C] px-2 py-1"
          >
            Close ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <p className="text-sm text-[#B5462F]">{error}</p>
          )}
          {!error && !data && (
            <p className="text-sm text-[#6B6358]">Compiling prompt…</p>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <Stat label="Model" value={data.model} />
                <Stat label="Pending slots" value={data.context_counts.pending_slots} />
                <Stat label="DNA lines" value={data.context_counts.brand_dna_statements} />
                <Stat label="References" value={data.context_counts.reference_creatives} />
              </div>
              {!data.template?.document_type_id && (
                <div className="mb-4 text-xs text-[#B5462F] bg-[#B5462F]/10 border border-[#B5462F]/30 rounded px-3 py-2">
                  Heads up — this template is not linked to a Document Type, so no
                  Reference Creatives will be injected. Link one in Brand Kit →
                  Templates.
                </div>
              )}

              <Section
                title="System prompt"
                value={data.system_prompt}
                testid={DESIGN_REQUESTS.previewPromptSystem}
                copyTestid={DESIGN_REQUESTS.previewPromptCopySystem}
                onCopy={() => copy(data.system_prompt)}
              />
              <Section
                title="User prompt"
                value={data.user_prompt}
                testid={DESIGN_REQUESTS.previewPromptUser}
                copyTestid={DESIGN_REQUESTS.previewPromptCopyUser}
                onCopy={() => copy(data.user_prompt)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#1A1F2C]/10 bg-white rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">{label}</div>
      <div className="font-mono text-sm text-[#1A1F2C] truncate">{value}</div>
    </div>
  );
}

function Section({ title, value, testid, copyTestid, onCopy }) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-xs uppercase tracking-[0.18em] text-[#3D3A35]">{title}</h3>
        <button
          type="button"
          onClick={onCopy}
          data-testid={copyTestid}
          className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F] hover:underline"
        >
          Copy
        </button>
      </div>
      <pre
        data-testid={testid}
        className="font-mono text-[11px] leading-relaxed text-[#1A1F2C] bg-white border border-[#1A1F2C]/10 rounded-md p-3 whitespace-pre-wrap overflow-x-auto"
      >
        {value || "(empty)"}
      </pre>
    </div>
  );
}
